import Cookies from 'js-cookie';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Flex, Box, Spacer, Tooltip, Icon, Text, Center, Skeleton, Spinner, Button, Fade, useBreakpointValue, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, useDisclosure, IconButton } from '@chakra-ui/react';
import { useLocation, useNavigate, useNavigation, Outlet, useRevalidator } from 'react-router-dom';
import urlToId from '../../helpers/UrlToId';
import useLocalStorage from 'react-use-localstorage';
import { FiHome, FiUsers, FiBuilding, FiCalendar, FiTool, FiCheckCircle, FiList, FiPlus, FiSettings } from 'react-icons/fi';
import { PiUsersThreeDuotone, PiUsersThreeLight } from 'react-icons/pi';
import { BsHouses, BsTable, BsKanban, BsGraphUp } from 'react-icons/bs';
import { TbBuildingCommunity, TbCalendarEvent, TbDevicesExclamation, TbArrowBarLeft, TbArrowBarRight, TbBulb, TbMenu2, TbDashboard, TbReportAnalytics, TbRefresh } from 'react-icons/tb';
import { HiOutlineOfficeBuilding, HiOutlineClipboardList } from 'react-icons/hi';
import { AiOutlineFileDone, AiOutlineProject } from 'react-icons/ai';
import { FaTasks } from 'react-icons/fa';
import { ChevronDownIcon, ChevronRightIcon, PlusSquareIcon } from '@chakra-ui/icons';
import { InfoBar } from "./info";
import AuthContext from '../../contexts/Auth';
import client from '../../helpers/Client';
import 'react-json-view-lite/dist/index.css';
import HeaderLinks from './NavbarLinksAdmin';
import Tutorial from '../Tour/Tutorial';
import { getTutorialSteps, getTutorialStorageKey } from '../Tour/tutorialSteps';
import { useTranslation } from 'react-i18next';



function NavbarMenuItem({ label, path, icon, variant = 'default' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = path === location.pathname;
  const [isHovered, setIsHovered] = useState(false);

  const baseStyles = {
    px: 4,
    py: 2,
    borderRadius: '8px',
    fontSize: 'sm',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    position: 'relative',
    whiteSpace: 'nowrap'
  };

  const variantStyles = {
    default: {
      color: isActive ? 'var(--primary-600)' : 'var(--text-default)',
      background: isActive ? 'var(--primary-50)' : 'transparent',
      _hover: {
        background: isActive ? 'var(--primary-100)' : 'var(--bg-subtle)',
        color: isActive ? 'var(--primary-700)' : 'var(--text-default)',
        transform: 'translateY(-1px)'
      }
    },
    accent: {
      color: 'white',
      background: 'linear-gradient(135deg, var(--accent-500), var(--accent-600))',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      _hover: {
        background: 'linear-gradient(135deg, var(--accent-600), var(--accent-700))',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)'
      }
    }
  };

  return (
    <Box
      {...baseStyles}
      {...variantStyles[variant]}
      onClick={() => navigate(path)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {icon && (
        <Icon
          as={icon}
          boxSize={4}
          transition='transform 0.2s ease'
          transform={isHovered ? 'scale(1.1)' : 'scale(1)'}
        />
      )}
      <Text>{label}</Text>
      {isActive && (
        <Box
          position='absolute'
          bottom='-8px'
          left='50%'
          transform='translateX(-50%)'
          width='20px'
          height='2px'
          background='var(--primary-500)'
          borderRadius='full'
        />
      )}
    </Box>
  );
}

function NavbarDropdown({ label, items }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <Box position='relative' ref={dropdownRef}>
      <Box
        px={4}
        py={2}
        borderRadius='8px'
        fontSize='sm'
        fontWeight='500'
        cursor='pointer'
        transition='all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
        display='flex'
        alignItems='center'
        gap={2}
        color='var(--text-default)'
        _hover={{
          color: 'white',
          background: 'var(--error-800)',
          transform: 'translateY(-1px)'
        }}
        onClick={isOpen ? onClose : onOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text>{label}</Text>
        <Icon
          as={ChevronDownIcon}
          boxSize={3}
          transition='transform 0.2s ease'
          transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
        />
      </Box>

      {isOpen && (
        <Box
          position='absolute'
          top='100%'
          left={0}
          mt={2}
          minW='200px'
          borderRadius='12px'
          boxShadow='var(--shadow-xl)'
          border='1px solid var(--border-default)'
          py={2}
          zIndex={9999}
          backdropFilter='blur(100px)'
        >
          {items.map((item, index) => (
            <NavbarDropdownItem key={index} {...item} onClose={onClose} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function NavbarDropdownItem({ label, path, icon, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = path === location.pathname;

  return (
    <Box
      px={4}
      py={2}
      cursor='pointer'
      transition='all 0.2s ease'
      display='flex'
      alignItems='center'
      gap={3}
      color={isActive ? 'var(--blue-100)' : 'var(--text-default)'}
      background={isActive ? 'var(--primary-50)' : 'var(--primary-50)'}
      _hover={{
        background: isActive ? 'var(--error-800)' : 'var(--error-800)',
        color: isActive ? 'var(--primary-50)' : 'var(--primary-50)'
      }}
      onClick={() => {
        navigate(path);
        onClose();
      }}
    >
      {icon && <Icon as={icon} boxSize={4} />}
      <Text fontSize='sm' fontWeight='500'>{label}</Text>
    </Box>
  );
}

function ModernNavbar({ onMobileMenuOpen, onOpenTutorial }) {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [disabledRoutes, setDisabledRoutes] = useState(new Set());
  const isSuperUser = !!auth.data?.user?.is_superuser;
  const userRoles = {
    manager: !!auth.data?.user?.employee?.is_manager,
    servicer: !!auth.data?.user?.is_servicer,
    technical: !!auth.data?.user?.is_technical,
    reseller: !!auth.data?.user?.is_reseller,
    employee: !!auth.data?.user?.is_employee,
  };
  const navigate = useNavigate();


  useEffect(() => {
    const load = async () => {
      try {
        if (isSuperUser) { setDisabledRoutes(new Set()); return; }
        const roles = Object.entries(userRoles).filter(([_, v]) => v).map(([k]) => k);
        if (!roles.length) { setDisabledRoutes(new Set()); return; }
        const { data } = await client.get(`/config/role-pages/?enabled=false`);
        const arr = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        const disabled = arr.filter(it => roles.includes(it.role_code)).map(it => it.route);
        setDisabledRoutes(new Set(disabled));
      } catch (_) {
        setDisabledRoutes(new Set());
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser]);

  const allow = (path) => {
    if (isSuperUser) return true;
    // Hide Employees and Customers for non-managers
    if (!userRoles.manager && (path === '/calisanlar/' || path === '/musteriler/')) {
      return false;
    }
    return !disabledRoutes.has(path);
  };


  return (
    <Flex
      id='navbar'
      px={{ base: 4, md: 6 }}
      py={{ base: 3, md: 4 }}
      background='var(--bg-elevated)'
      backdropFilter='blur(20px)'
      borderBottom='1px solid var(--border-default)'
      boxShadow='var(--shadow-md)'
      justifyContent='space-between'
      alignItems='center'
      position='sticky'
      top={0}
      zIndex={100}
      transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      className='theme-card'
    >
      {/* Logo ve Brand */}
      <Flex alignItems='center' gap={6}>
        <Box>
          <Text fontWeight={'700'} fontSize={{ base: 'xl', md: '2xl' }} letterSpacing='-0.025em'>
            <Text
              color='var(--error-800)'
              as="span"
              fontSize="inherit"
            >
              ino
            </Text>
            <Text
              color='var(--text-default)'
              as="span"
              fontSize="inherit"
            >
              hom
            </Text>
          </Text>
          <Box
            width='60px'
            height='3px'
            background='linear-gradient(90deg, var(--primary-500), var(--accent-500))'
            borderRadius='full'
            mt={1}
            display={{ base: 'none', sm: 'block' }}
          />
        </Box>

        {/* Desktop Navigation Menu */}
        {!isMobile && (
          <Flex alignItems='center' gap={1}>
            {allow('/ana-sayfa/') && (
              <NavbarMenuItem label={t('navigation.dashboard')} path='/ana-sayfa/' icon={TbDashboard} />
            )}
            {(() => {
              const managementItems = [
                { label: t('navigation.employees'), path: '/calisanlar/', icon: FiUsers },
                { label: t('navigation.customers'), path: '/musteriler/', icon: PiUsersThreeDuotone },
                { label: t('navigation.projects'), path: '/proje/', icon: AiOutlineProject },
                { label: t('navigation.resellers'), path: '/bayi/', icon: HiOutlineOfficeBuilding },
                { label: t('navigation.serviceForms'), path: '/servis/kayitlar/', icon: HiOutlineClipboardList }
              ].filter(it => allow(it.path));
              return managementItems.length ? (
                <NavbarDropdown label={t('navigation.management')} items={managementItems} />
              ) : null;
            })()}
            {(() => {
              const operationItems = [
                { label: t('navigation.tickets'), path: '/is-emri/', icon: HiOutlineClipboardList },
                { label: t('navigation.completedTickets'), path: '/tamamlanmis-is-emri/', icon: FiCheckCircle },
                { label: t('navigation.tasks'), path: '/gorevler/', icon: BsKanban },
                { label: t('navigation.appointments'), path: '/randevular/', icon: FiCalendar },
                { label: t('navigation.services'), path: '/servis/', icon: FiTool },
                { label: 'Harita', path: '/harita/', icon: BsGraphUp },
                { label: 'Formlar', path: '/formlar/', icon: HiOutlineClipboardList }
              ].filter(it => allow(it.path));
              return operationItems.length ? (
                <NavbarDropdown label={t('navigation.operations')} items={operationItems} />
              ) : null;
            })()}
            {(() => {
              const formItems = [
                { label: 'Formlar', path: '/formlar/', icon: HiOutlineClipboardList },
                { label: 'Form Ekle', path: '/formlar/yeni', icon: FiPlus },
                { label: 'Teknisyenler', path: '/formlar/teknisyenler', icon: FiUsers },
                { label: 'İstatistik', path: '/formlar/istatistik', icon: TbReportAnalytics },
              ].filter(it => allow(it.path));
              return formItems.length ? (
                <NavbarDropdown label={'Form'} items={formItems} />
              ) : null;
            })()}
            {allow('/is-emri/olustur/') && (
              <NavbarMenuItem
                label={t('navigation.newTicket')}
                path='/is-emri/olustur/'
                icon={FiPlus}
                variant='accent'
              />
            )}
          </Flex>
        )}

        {/* Mobile menu button */}
        {isMobile && (
          <IconButton
            icon={<TbMenu2 />}
            onClick={onMobileMenuOpen}
            variant='ghost'
            size='md'
            color='var(--text-default)'
            _hover={{
              background: 'var(--bg-subtle)',
              color: 'var(--text-default)',
              transform: 'translateY(-1px)'
            }}
            borderRadius='8px'
            transition='all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            className='theme-button'
          />
        )}
      </Flex>

      <Flex alignItems='center' gap={3}>
        <InfoBar />

        {!isMobile ? (
          <Tooltip label={t('öğretici')} aria-label={t('öğretici')}>
            <IconButton
              border={'2px solid '}
              icon={<TbBulb />}
              aria-label={t('öğretici')}
              variant='solid'
              size={'md'}
              onClick={onOpenTutorial}
            />
          </Tooltip>

        ) : (
          <IconButton
            icon={<TbBulb />}
            aria-label={t('common.tutorial')}
            variant='ghost'
            ßß size='md'
            onClick={onOpenTutorial}
          />
        )}

        <HeaderLinks />
        <Tooltip label={'Önbelleği yenile'} aria-label={'Önbelleği yenile'}>
          <IconButton
            icon={<TbRefresh />}
            aria-label={'Önbelleği yenile'}
            variant='ghost'
            size='md'
            onClick={() => {
              try { localStorage.clear(); } catch (_) { }
              window.location.reload();
            }}
          />
        </Tooltip>
      </Flex>
    </Flex>
  );
}
function RequireAuth({ children }) {
  const token = Cookies.get('token');
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [storageUser, setStorageUser] = useLocalStorage('user');

  useEffect(() => {
    if (!token) {
      navigate('/giris/');
      return;
    }

    if (auth.data) return;
    if (storageUser && storageUser.token === token) {
      auth.setData(JSON.parse(storageUser));
      return;
    }
    client
      .get('/users/?is_me=true')
      .then((response) => {
        const user = { user: response.data[0] };
        auth.setData(user);
        setStorageUser(JSON.stringify(user));
      })
      .catch(() => {
        navigate('/giris/');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, auth, navigate]);

  if (!token) {
    return <Spinner />;
  }
  if (auth.data?.user) return children;
  return <Spinner />;
}

function MenuItem({ label, path, icon, children, onClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = path === location.pathname;
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const baseProps = {
    cursor: 'pointer',
    borderRadius: '12px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid transparent'
  };

  const activeProps = isActive ? {
    background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    color: 'white',
    boxShadow: '0 8px 25px -8px rgba(14, 165, 233, 0.4)',
    transform: 'translateX(6px) scale(1.02)',
    borderColor: '#38bdf8'
  } : {
    background: 'var(--bg-elevated)',
    color: 'var(--text-default)',
    _hover: {
      background: 'var(--bg-subtle)',
      color: 'var(--text-default)',
      transform: 'translateX(4px) scale(1.01)',
      boxShadow: 'var(--shadow-md)',
      borderColor: 'var(--border-accent)'
    }
  };

  const clickHandler = () => {
    if (path) {
      navigate(path);
      if (onClick) onClick();
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <Box mb={2}>
      <Flex
        p={4}
        gap={3}
        flexWrap={'nowrap'}
        alignItems='center'
        {...baseProps}
        {...activeProps}
        onClick={clickHandler}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: isActive ? 0 : '-100%',
          width: '3px',
          height: '100%',
          background: 'linear-gradient(180deg, #10b981, #059669)',
          borderRadius: '0 2px 2px 0',
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        _after={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isActive
            ? 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))'
            : 'transparent',
          pointerEvents: 'none',
          transition: 'background 0.2s ease'
        }}
      >
        {icon && (
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            w={10}
            h={8}
            borderRadius='lg'
            bg={isActive ? 'rgba(255,255,255,0.2)' : (isHovered ? 'var(--primary-100)' : 'transparent')}
            transition='all 0.2s ease'
          >
            <Icon
              as={icon}
              boxSize={5}
              color={isActive ? 'white' : (isHovered ? 'var(--primary-600)' : 'var(--gray-600)')}
              transition='all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              transform={isHovered || isActive ? 'scale(1.1)' : 'scale(1)'}
            />
          </Box>
        )}
        <Text
          fontSize='sm'
          fontWeight={isActive ? '600' : '500'}
          whiteSpace={'nowrap'}
          color={'inherit'}
          textDecoration='inherit'
          letterSpacing='-0.025em'
          position='relative'
          zIndex={2}
        >
          {label}
        </Text>
        {children && (
          <>
            <Spacer />
            <Icon
              as={isOpen ? ChevronDownIcon : ChevronRightIcon}
              boxSize={4}
              transition='transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              transform={isOpen ? 'rotate(90deg)' : 'rotate(0deg)'}
              color={isActive ? 'white' : 'var(--gray-500)'}
            />
          </>
        )}
      </Flex>
      {children && isOpen && (
        <Box
          ml={6}
          mt={2}
          pl={4}
          borderLeft='2px solid var(--gray-200)'
          className='animate-slide-up'
        >
          {children}
        </Box>
      )}
    </Box>
  )
}



// Mobile Sidebar Component (Drawer)
function MobileSideBar({ isOpen, onClose }) {
  return (
    <Drawer isOpen={isOpen} placement='left' onClose={onClose} size='sm'>
      <DrawerOverlay bg='rgba(0, 0, 0, 0.4)' backdropFilter='blur(4px)' />
      <DrawerContent
        background='rgba(255, 255, 255, 0.98)'
        backdropFilter='blur(20px)'
        borderRight='1px solid rgba(255, 255, 255, 0.2)'
        boxShadow='var(--shadow-xl)'
      >
        <DrawerCloseButton
          color='var(--gray-600)'
          _hover={{
            background: 'var(--gray-100)',
            color: 'var(--gray-800)'
          }}
          borderRadius='var(--radius-lg)'
        />
        <DrawerHeader
          borderBottomWidth='1px'
          borderColor='var(--gray-200)'
          pb={4}
        >
          <Text
            fontSize='xl'
            fontWeight='700'
            bgGradient='linear(135deg, var(--primary-600), var(--accent-600))'
            bgClip='text'
            letterSpacing='-0.02em'
          >
            Menü
          </Text>
        </DrawerHeader>
        <DrawerBody p={4}>
          <SideBarContent onItemClick={onClose} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

// Shared Sidebar Content
function SideBarContent({ onItemClick }) {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const [disabledRoutes, setDisabledRoutes] = useState(new Set());
  const isSuperUser = !!auth.data?.user?.is_superuser;
  const userRoles = {
    manager: !!auth.data?.user?.employee?.is_manager,
    servicer: !!auth.data?.user?.is_servicer,
    technical: !!auth.data?.user?.is_technical,
    reseller: !!auth.data?.user?.is_reseller,
    employee: !!auth.data?.user?.is_employee,
  };
  useEffect(() => {
    const load = async () => {
      try {
        if (isSuperUser) { setDisabledRoutes(new Set()); return; }
        const roles = Object.entries(userRoles).filter(([_, v]) => v).map(([k]) => k);
        if (!roles.length) { setDisabledRoutes(new Set()); return; }
        const { data } = await client.get(`/config/role-pages/?enabled=false`);
        const arr = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        const disabled = arr.filter(it => roles.includes(it.role_code)).map(it => it.route);
        setDisabledRoutes(new Set(disabled));
      } catch (_) {
        setDisabledRoutes(new Set());
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser]);
  const allow = (path) => isSuperUser || !disabledRoutes.has(path);

  return (
    <Flex flexDir='column' gap={2} flexGrow={1}>
      <Box mb={4}>
        {/* Dashboard */}
        <Box mb={4}>
          {allow('/ana-sayfa/') && (
            <MenuItem label={t('navigation.dashboard')} path='/ana-sayfa/' icon={TbDashboard} onClick={onItemClick} />
          )}
        </Box>

        {/* Yönetim Kategorisi */}
        <Box mb={4}>
          <Text
            fontSize='xs'
            fontWeight='700'
            color='var(--gray-500)'
            textTransform='uppercase'
            letterSpacing='0.1em'
            mb={3}
            px={3}
            display='flex'
            alignItems='center'
            gap={2}
          >
            <Box
              w={2}
              h={2}
              borderRadius='full'
              bg='var(--primary-400)'
            />
            {t('navigation.management')}
          </Text>
          {allow('/calisanlar/') && (<MenuItem label={t('navigation.employees')} path='/calisanlar/' icon={FiUsers} onClick={onItemClick} />)}
          {allow('/musteriler/') && (<MenuItem label={t('navigation.customers')} path='/musteriler/' icon={PiUsersThreeDuotone} onClick={onItemClick} />)}
          {allow('/proje/') && (<MenuItem label={t('navigation.projects')} path='/proje/' icon={AiOutlineProject} onClick={onItemClick} />)}
          {allow('/bayi/') && (<MenuItem label={t('navigation.resellers')} path='/bayi/' icon={HiOutlineOfficeBuilding} onClick={onItemClick} />)}
        </Box>

        {/* İşlemler Kategorisi */}
        <Box mb={4}>
          <Text
            fontSize='xs'
            fontWeight='700'
            color='var(--gray-500)'
            textTransform='uppercase'
            letterSpacing='0.1em'
            mb={3}
            px={3}
            display='flex'
            alignItems='center'
            gap={2}
          >
            <Box
              w={2}
              h={2}
              borderRadius='full'
              bg='var(--accent-400)'
            />
            {t('navigation.operations')}
          </Text>
          {allow('/is-emri/') && (<MenuItem label={t('navigation.tickets')} path='/is-emri/' icon={HiOutlineClipboardList} onClick={onItemClick} />)}
          {allow('/tamamlanmis-is-emri/') && (<MenuItem label={t('navigation.completedTickets')} path='/tamamlanmis-is-emri/' icon={FiCheckCircle} onClick={onItemClick} />)}
          {allow('/gorevler/') && (<MenuItem label={t('navigation.tasks')} path='/gorevler/' icon={BsKanban} onClick={onItemClick} />)}
          {allow('/randevular/') && (<MenuItem label={t('navigation.appointments')} path='/randevular/' icon={FiCalendar} onClick={onItemClick} />)}
          {allow('/servis/') && (<MenuItem label={t('navigation.services')} path='/servis/' icon={FiTool} onClick={onItemClick} />)}
          {/* Form grubu */}
          <MenuItem label={'Form'} icon={HiOutlineClipboardList}>
            {allow('/formlar/') && (<MenuItem label={'Formlar'} path='/formlar/' icon={HiOutlineClipboardList} onClick={onItemClick} />)}
            {allow('/formlar/yeni') && (<MenuItem label={'Form Ekle'} path='/formlar/yeni' icon={FiPlus} onClick={onItemClick} />)}
            {allow('/formlar/teknisyenler') && (<MenuItem label={'Teknisyenler'} path='/formlar/teknisyenler' icon={FiUsers} onClick={onItemClick} />)}
            {allow('/formlar/istatistik') && (<MenuItem label={'İstatistik'} path='/formlar/istatistik' icon={TbReportAnalytics} onClick={onItemClick} />)}
          </MenuItem>
        </Box>

        {/* Hızlı İşlemler */}
        <Box mb={4}>
          <Text
            fontSize='xs'
            fontWeight='700'
            color='var(--gray-500)'
            textTransform='uppercase'
            letterSpacing='0.1em'
            mb={3}
            px={3}
            display='flex'
            alignItems='center'
            gap={2}
          >
            <Box
              w={2}
              h={2}
              borderRadius='full'
              bg='var(--success-400)'
            />
            {t('navigation.quickActions')}
          </Text>
          {allow('/is-emri/olustur/') && (<MenuItem label={t('navigation.newTicket')} path='/is-emri/olustur/' icon={PlusSquareIcon} onClick={onItemClick} />)}
        </Box>

        {/* Raporlar */}

      </Box>
    </Flex>
  );
}

const Loader = ({ isLoading = false }) => {
  const { t } = useTranslation();
  return (
    <Fade in={isLoading}>
      <Flex gap={1} alignItems={'center'} position={'absolute'} bottom={5} right={5} bg={'yellow'} borderWidth={1} px={5} py={2} borderRadius={5}>
        <Spinner size={'sm'} />
        <Text>{t('common.loading')}</Text>
      </Flex>
    </Fade>
  )
}

export default function DashboardLayout() {
  const navigation = useNavigation();
  const location = useLocation();
  const revalidator = useRevalidator();
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const steps = React.useMemo(() => getTutorialSteps(location.pathname), [location.pathname]);
  const auth = useContext(AuthContext);
  const [disabledRoutes, setDisabledRoutes] = useState(new Set());
  const isSuperUser = !!auth?.data?.user?.is_superuser;
  const userRoles = {
    manager: !!auth?.data?.user?.employee?.is_manager,
    servicer: !!auth?.data?.user?.is_servicer,
    technical: !!auth?.data?.user?.is_technical,
    reseller: !!auth?.data?.user?.is_reseller,
    employee: !!auth?.data?.user?.is_employee,
  };

  useEffect(() => {
    try {
      const v = window.localStorage.getItem('settings.accessibility.fontSize') || 'medium';
      const size = v === 'small' ? '14px' : (v === 'large' ? '18px' : '16px');
      document.documentElement.style.fontSize = size;
    } catch (_) { }
  }, []);

  useEffect(() => {
    let ws;
    let es;
    try {
      const resellerUrl = auth?.data?.user?.employee?.reseller?.url;
      const resellerId = resellerUrl ? urlToId(resellerUrl) : null;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const host = window.location.host;
      const qp = resellerId ? `?reseller=${resellerId}` : '';
      ws = new WebSocket(`${proto}://${host}/ws/dashboard/${qp}`);
      let queued = false;
      const schedule = () => {
        if (queued) return;
        queued = true;
        setTimeout(() => {
          try { revalidator.revalidate(); } catch (_) { }
          queued = false;
        }, 500);
      };
      ws.onmessage = () => {
        schedule();
      };
      ws.onerror = () => {
        try { ws && ws.close(); } catch (_) { }
        // Fallback to SSE
        try {
          es = new EventSource("/events/");
          es.onmessage = () => schedule();
          es.onerror = () => { try { es.close(); } catch (_) { } };
        } catch (_) { }
      };
    } catch (_) { }
    return () => { try { ws && ws.close(); } catch (_) { }; try { es && es.close(); } catch (_) { } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.data?.user?.employee?.reseller?.url]);

  useEffect(() => {
    const load = async () => {
      try {
        if (isSuperUser) { setDisabledRoutes(new Set()); return; }
        const roles = Object.entries(userRoles).filter(([_, v]) => v).map(([k]) => k);
        if (!roles.length) { setDisabledRoutes(new Set()); return; }
        const { data } = await client.get(`/config/role-pages/?enabled=false`);
        const arr = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        const disabled = arr.filter(it => roles.includes(it.role_code)).map(it => it.route);
        setDisabledRoutes(new Set(disabled));
      } catch (_) {
        setDisabledRoutes(new Set());
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser]);
  const isDenied = !isSuperUser && disabledRoutes.has(location.pathname);



  return (
    <RequireAuth>
      <Loader isLoading={navigation.state === 'loading'} />
      <Flex
        h='100vh'
        flexDir={'column'}
        id='page'
        background='linear-gradient(135deg, var(--gray-50) 0%, var(--neutral-50) 100%)'
        position='relative'
        overflow='hidden'
      >
        {/* Background pattern */}
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.03}
          backgroundImage='radial-gradient(circle at 25px 25px, var(--primary-500) 2px, transparent 0), radial-gradient(circle at 75px 75px, var(--accent-500) 2px, transparent 0)'
          backgroundSize='100px 100px'
          pointerEvents='none'
        />

        <ModernNavbar onMobileMenuOpen={onMobileMenuOpen} onOpenTutorial={() => setIsTutorialOpen(true)} />

        <Flex id='main' overflowY='scroll' h='100%' position='relative'>
          <MobileSideBar isOpen={isMobileMenuOpen} onClose={onMobileMenuClose} />
          <Box
            flex={1}
            p={{ base: 4, md: 6 }}
            overflowY='auto'
            className='animate-fade-in'
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'var(--gray-100)',
                borderRadius: 'var(--radius-full)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(135deg, var(--primary-400), var(--primary-600))',
                borderRadius: 'var(--radius-full)',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'linear-gradient(135deg, var(--primary-500), var(--primary-700))',
              }
            }}
          >
            <Box
              maxWidth={{ base: '100%', lg: '1400px' }}
              mx='auto'
              position='relative'
            >
              {isDenied ? (
                <Center><Text color="red.600" fontWeight="bold">Bu sayfayı görüntülemeye izniniz yoktur.</Text></Center>
              ) : (
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              )}
              <Tutorial
                isOpen={isTutorialOpen}
                steps={steps}
                onClose={() => {
                  try {
                    const storageKey = getTutorialStorageKey(location.pathname);
                    window.localStorage.setItem(storageKey, 'true');
                  } catch (_) { }
                  setIsTutorialOpen(false);
                }}
              />
            </Box>
          </Box>
        </Flex>
      </Flex>
    </RequireAuth>
  )
}
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, info) { }
  render() {
    if (this.state.hasError) {
      return (
        <Center><Text color="red.600" fontWeight="bold">Bir hata oluştu</Text></Center>
      );
    }
    return this.props.children;
  }
}
